// Shared recipe presentation used by Cook detail + Admin/Nutritionist preview.
// Produces a polished read-only view with:
//  - icon-led section headings
//  - ingredients rendered as pills (special chars stripped)
//  - cooking instructions split into numbered steps on blank-line gaps
//  - linked recipes shown as navigable chips (back returns to current recipe)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Utensils, ChefHat, Sparkles, StickyNote, Soup, Hash, Users, Clock, Link2, Truck, AlertTriangle } from "lucide-react";
import { API } from "../App";

/* ---------- text utilities ---------- */

// Strip leading bullet/number prefixes and surrounding whitespace from one item.
// Also drops any leftover special characters (keeps letters, digits, basic
// punctuation like %, /, ., commas, parentheses, hyphens — meaningful for
// recipes like "1/2 cup" or "salt to taste, optional").
function cleanIngredient(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  // Drop leading bullet glyphs / digit-prefixes like "1.", "1)", "- ", "* ", "• "
  s = s.replace(/^[\s\u2022\u2023\u25E6\u2043\u2219\-\*\u2192\u2013\u2014>•·]+\s*/u, "");
  s = s.replace(/^\d+[\.\)]\s+/, "");
  // Drop characters that are clearly noise — emojis and exotic symbols
  s = s.replace(/[\u{1F300}-\u{1FAFF}\u{2700}-\u{27BF}]/gu, "");
  return s.replace(/\s+/g, " ").trim();
}

export function splitIngredients(text) {
  if (!text) return [];
  return text
    .split(/[\n,;]+/) // lines, commas, semicolons all valid separators
    .map(cleanIngredient)
    .filter(Boolean);
}

export function splitSteps(text) {
  if (!text) return [];
  // First try blank-line gaps (2+ consecutive newlines) — the primary signal.
  const blocks = text.split(/\n\s*\n+/).map((b) => b.trim()).filter(Boolean);
  if (blocks.length > 1) return blocks;
  // Otherwise fall back to one step per non-empty line.
  return text
    .split(/\n+/)
    .map((s) => s.replace(/^\s*\d+[\.\)]\s+/, "").trim())
    .filter(Boolean);
}

/* ---------- subcomponents ---------- */

function SectionHead({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
        <Icon size={14} />
      </div>
      <h2 className="text-sm font-extrabold uppercase tracking-wider text-gray-900">{label}</h2>
      {typeof count === "number" && count > 0 && (
        <span className="text-[10px] font-bold text-gray-400">· {count}</span>
      )}
    </div>
  );
}

function IngredientPills({ raw }) {
  const items = splitIngredients(raw);
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2" data-testid="recipe-ingredient-pills">
      {items.map((ing, idx) => (
        <li
          key={`${ing}-${idx}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5"
          data-testid={`recipe-ingredient-${idx}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden />
          {ing}
        </li>
      ))}
    </ul>
  );
}

function NumberedSteps({ raw }) {
  const steps = splitSteps(raw);
  if (steps.length === 0) return null;
  return (
    <ol className="space-y-3" data-testid="recipe-steps-list">
      {steps.map((step, idx) => (
        <li key={idx} className="flex items-start gap-3" data-testid={`recipe-step-${idx}`}>
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
            {idx + 1}
          </div>
          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap flex-1">{step}</p>
        </li>
      ))}
    </ol>
  );
}

function MetaTile({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 bg-gray-50 rounded-xl p-3 border border-gray-100">
      <div className="w-8 h-8 rounded-lg bg-white text-emerald-700 flex items-center justify-center border border-gray-200">
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">{label}</p>
        <p className="text-sm font-bold text-gray-900 truncate">{value || "—"}</p>
      </div>
    </div>
  );
}

/* ---------- main component ---------- */

export default function RecipeDisplay({
  recipe,
  category,
  tagsById = {},
  kitchensById = {},
  rightAction = null,
  linkBasePath = null, // e.g. "/cook/recipes" — used as `${linkBasePath}/${recipe_id}${linkPathSuffix}`
  linkPathSuffix = "",  // e.g. "/preview" for admin preview routes
  currentPath = null,  // pass location.pathname so back returns to THIS recipe
}) {
  const [linkedRecipes, setLinkedRecipes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = recipe?.linked_recipe_ids || [];
      if (!ids.length) { if (!cancelled) setLinkedRecipes([]); return; }
      try {
        const results = await Promise.all(
          ids.map((rid) =>
            fetch(`${API}/recipes/${rid}`, { credentials: "include" })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null),
          ),
        );
        if (!cancelled) setLinkedRecipes(results.filter(Boolean));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [recipe?.recipe_id, recipe?.linked_recipe_ids]);

  if (!recipe) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 space-y-6" data-testid="recipe-display">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight">{recipe.name}</h1>
          <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1.5">
            <Soup size={11} className="text-emerald-600" />
            {category?.name || "Uncategorised"}
          </p>
        </div>
        {rightAction}
      </div>

      {/* Description (optional) */}
      {recipe.description && (
        <p className="text-sm text-gray-600 leading-relaxed border-l-2 border-emerald-200 pl-3 italic" data-testid="recipe-description">
          {recipe.description}
        </p>
      )}

      {/* Meta tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetaTile icon={Users} label="Serving size" value={recipe.serving_size} />
        <MetaTile
          icon={Hash}
          label="Operational"
          value={(() => {
            const unit = recipe.operational_unit || recipe.portion_type || "";
            const val = recipe.operational_value;
            if (!unit && (val === null || val === undefined || val === "")) return "—";
            if (val === null || val === undefined || val === "") return unit || "—";
            return `${val} ${unit}`.trim();
          })()}
        />
        <MetaTile
          icon={Soup}
          label="Metric"
          value={(() => {
            const unit = recipe.metric_unit || "";
            const val = recipe.metric_value;
            if (!unit && (val === null || val === undefined || val === "")) return "—";
            if (val === null || val === undefined || val === "") return unit || "—";
            return `${val} ${unit}`.trim();
          })()}
        />
        <MetaTile icon={Clock} label="Last updated" value={(recipe.updated_at || "").slice(0, 10)} />
      </div>

      {/* Tags + Kitchens row */}
      {(recipe.tag_ids?.length > 0 || recipe.kitchen_ids?.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {(recipe.tag_ids || []).map((tid) => {
            const t = tagsById[tid];
            if (!t) return null;
            return (
              <span
                key={tid}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md"
                style={{ backgroundColor: `${t.color_hex}1F`, color: t.color_hex }}
              >
                <Star size={9} /> {t.name}
              </span>
            );
          })}
          {(recipe.kitchen_ids || []).map((kid) => {
            const k = kitchensById[kid];
            if (!k) return null;
            return (
              <span key={kid} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-blue-50 text-blue-700">
                <ChefHat size={9} /> {k.kitchen_code}
              </span>
            );
          })}
        </div>
      )}

      {/* Ingredients */}
      {recipe.ingredients && (
        <section>
          <SectionHead icon={Utensils} label="Ingredients" count={splitIngredients(recipe.ingredients).length} />
          <IngredientPills raw={recipe.ingredients} />
        </section>
      )}

      {/* Cooking steps */}
      {recipe.cooking_instructions && (
        <section>
          <SectionHead icon={ChefHat} label="Method" count={splitSteps(recipe.cooking_instructions).length} />
          <NumberedSteps raw={recipe.cooking_instructions} />
        </section>
      )}

      {/* Delivery instructions — high-visibility yellow callout for kitchen staff.
          Sits AFTER ingredients/method so it acts as a final reminder before
          packing. Eye-catching but not screaming, with an icon for scan-ability. */}
      {recipe.delivery_instructions && (
        <section data-testid="recipe-delivery-instructions">
          <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-200/60 border-b border-amber-300">
              <Truck size={15} className="text-amber-800" />
              <span className="text-xs font-extrabold uppercase tracking-wider text-amber-900">Delivery Instructions</span>
              <AlertTriangle size={13} className="text-amber-800 ml-auto" />
            </div>
            <p className="px-4 py-3 text-sm text-amber-950 leading-relaxed whitespace-pre-wrap">{recipe.delivery_instructions}</p>
          </div>
        </section>
      )}

      {/* Linked recipes — chips that navigate, with state.from pointing back here */}
      {linkedRecipes.length > 0 && (
        <section data-testid="recipe-linked-section">
          <SectionHead icon={Link2} label="Linked recipes" count={linkedRecipes.length} />
          <div className="flex flex-wrap gap-2">
            {linkedRecipes.map((lr) =>
              linkBasePath ? (
                <Link
                  key={lr.recipe_id}
                  to={`${linkBasePath}/${lr.recipe_id}${linkPathSuffix}`}
                  state={currentPath ? { from: currentPath } : undefined}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 hover:bg-emerald-100 transition-colors"
                  data-testid={`recipe-linked-${lr.recipe_id}`}
                >
                  <Link2 size={11} className="text-emerald-600" />
                  {lr.name}
                </Link>
              ) : (
                <span
                  key={lr.recipe_id}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5"
                  data-testid={`recipe-linked-${lr.recipe_id}`}
                >
                  <Link2 size={11} className="text-emerald-600" />
                  {lr.name}
                </span>
              ),
            )}
          </div>
        </section>
      )}

      {/* Notes */}
      {recipe.notes && (
        <section>
          <SectionHead icon={StickyNote} label="Notes" />
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-amber-50/40 border border-amber-100 rounded-xl p-3">
            <Sparkles size={12} className="inline mr-1 text-amber-500" />
            {recipe.notes}
          </p>
        </section>
      )}
    </div>
  );
}
